import type * as VerdiCommon from "verdi-common";
import { DataTypeConfig } from "verdi-common";
import { VertexInfo } from "verdi-common/schemas/commonSubSchemas/vertexInfo";
import { dayMs, minuteMs } from "verdi-common/timeConstants";
import { HistoricalDataAPI } from "../../APICommands/HistoricalDataAPI.js";
import type { HistoricalDataResponse } from "../../APICommands/HistoricalDataTypes.js";
import { sleep } from "verdi-common/utils/timeoutUtils";
import { LinkedDataListItem, PermSortedLinkedList } from "../../HelperFunctions/PermanentlySortedList.js";
import { ModelBase } from "../ModelBase.js";

export type DataTypeSpecifier = VerdiCommon.DataTypeSpecifier;

type RangeSpecifier = {
    start: Date;
    end: Date;
};

/**
 * Generates a number based on a position for easier equality checking between positions.
 * Does not have to be in sync with the similar function on the backend, as long as it outputs values that change
 * as positions change, this is fine.
 *
 * This function was added to fully decouple the front end from the backends positions hashing so we could finally
 * delete it from Timescale
 * @param position
 */
const hashPosition = (position: VertexInfo): number => {
    const SignificantDigits = 300000;
    const coords = [position.lat ?? 0, position.lng ?? 0];
    let response = 0;
    for (let i = 0; i < 2; i++) {
        coords[i] = (coords[i] * SignificantDigits) % SignificantDigits;
        response += Math.floor(coords[i]) * SignificantDigits ** i;
    }
    if (position.depth) {
        const hashDepth = (position.depth * 100) % 10000;
        response += SignificantDigits ** 2 * hashDepth;
    }
    return response;
};
export class DataPoint extends LinkedDataListItem {
    constructor(date: Date, parent: HistoricalDataBase, data: Record<string, number>) {
        super(data, undefined, false);
        this.date = date;
        this.parent = parent;
        this.data = data;
    }
    // source: ModelBase;
    date: Date;
    parent: HistoricalDataBase;

    getDeviceData(target: ModelBase | string): Partial<Record<DataTypeSpecifier, number>> {
        const keys = Object.keys(this.parent.dataKeyMetadataLookup);

        const returnValue: Partial<Record<DataTypeSpecifier, number>> = {
            moisture: null,
            moisture2: null,
            valveState: null,
            irrigationAmount: null,
            avgNDVI: null,
            ndviVariance: null,
            valveOpenPercent: null,
            zoneIrrigationScheduled: null,
            dailyIrrigationAmount: null,
            rainfallAmount: null,
            temperature: null,
        };
        const allData = this.getAllData();
        for (let i = 0; i < keys.length; i++) {
            const dataKey = keys[i];
            const cur = this.parent.dataKeyMetadataLookup[dataKey];
            if (cur.source === target) {
                returnValue[cur.dataType] = allData[dataKey];
            }
            if (target instanceof ModelBase && cur.source === target.id && !returnValue[cur.dataType]) {
                returnValue[cur.dataType] = allData[dataKey];
            }
        }
        return returnValue;
    }
}
const defaultmaximumMillisecondsDeltaT = minuteMs * 3;
export class HistoricalDataBase {
    sources: (ModelBase | string)[];
    dataTypes: DataTypeSpecifier[];
    downloadedRange: RangeSpecifier[] = [];
    maximumMillisecondsDeltaT = defaultmaximumMillisecondsDeltaT;
    compressedRequests = true;
    uid: string;
    anchorEveryXPoints = 30;
    dataKeyMetadataLookup: Record<string, HistoricalDataResponse> = {};

    /**
     * @deprecated
     * @return {Record<string, HistoricalDataResponse>}
     */
    get dataKeys() {
        console.warn("dataKeys is deprecated. Please use dataKeyMetadataLookup");
        return this.dataKeyMetadataLookup;
    }
    //TODO: store models
    data: PermSortedLinkedList<DataPoint>;
    worryAboutPosition = true;
    worryAboutRelevantZones = true;
    relevantZoneIDs = undefined;

    /**
     * If this is defined, any historical data objects that do not have a relevant
     * zone in this list will be discarded. This will not affect
     * Historical Data Objects with no relvant zones defined or an empty list
     * of relevant zones. See also allowBlankZone
     * @deprecated
     * @type {string[]}
     */
    acceptableZones: string[] = undefined;
    constructor(
        sources: (ModelBase | string)[],
        dataTypes: DataTypeSpecifier[],
        maximumMillisecondsDeltaT = defaultmaximumMillisecondsDeltaT,
        {
            relevantZoneIDs = undefined,
            worryAboutPosition = false,
            worryAboutRelevantZones = false,
            compressedRequests = true,
        } = {},
    ) {
        this.sources = sources;
        this.dataTypes = dataTypes;
        this.maximumMillisecondsDeltaT = maximumMillisecondsDeltaT;
        this.worryAboutPosition = worryAboutPosition;
        this.compressedRequests = compressedRequests;
        this.relevantZoneIDs = relevantZoneIDs;
        this.worryAboutRelevantZones = worryAboutRelevantZones;

        this.data = new PermSortedLinkedList<DataPoint>((v) => v.date.valueOf());
    }
    _queue: Promise<unknown>[] = [];
    async _waitInQueue(): Promise<() => undefined> {
        let resolution;
        const promise = new Promise((resolve) => {
            resolution = resolve;
        });

        this._queue.push(promise);

        for (let i = 0; i < this._queue.length; i++) {
            if (this._queue[i] === promise) {
                return resolution;
            } else {
                // Intentionally swallow errors from earlier queued promises so the queue keeps moving.
                // When debugTiming is enabled, log these errors to aid debugging.
                await this._queue[i].catch((error) => {
                    if (this.debugTiming) {
                        // eslint-disable-next-line no-console
                        console.error("HistoricalDataBase._waitInQueue: queued promise rejected", error);
                    }
                });
            }
        }
        return resolution;
    }
    debugTiming = false;
    async _addDataFromServer(data: HistoricalDataResponse[]): Promise<void> {
        const startTime = Date.now();
        const flatData = [];
        //flatten data into single list
        data.forEach((dataSet) => {
            dataSet.startDate = new Date(dataSet.startDate);
            dataSet.endDate = new Date(dataSet.startDate);

            // Update keys so data can be found
            const keyList: (number | string)[] = [dataSet.source, dataSet.dataType];

            // Only add positionHash if position is present
            if (dataSet?.position) {
                dataSet.positionHash = hashPosition(dataSet.position);
            }

            if (this.worryAboutPosition) {
                keyList.push(dataSet.positionHash);
            }
            if (this.worryAboutRelevantZones) {
                keyList.push(dataSet.relevantZonesHash);
            }
            const key = keyList.join("@");

            if (!this.dataKeyMetadataLookup[key]) {
                this.dataKeyMetadataLookup[key] = { ...dataSet };
                delete this.dataKeyMetadataLookup[key].data;
            }

            this.dataKeyMetadataLookup[key].startDate = new Date(
                Math.min(this.dataKeyMetadataLookup[key].startDate.valueOf(), dataSet.startDate.valueOf()),
            );

            this.dataKeyMetadataLookup[key].endDate = new Date(
                Math.max(this.dataKeyMetadataLookup[key].endDate.valueOf(), dataSet.endDate.valueOf()),
            );

            //Set the points up as a flattened array
            const newPoints = dataSet.data.map((d) => {
                const val = {
                    // @ts-ignore
                    date: new Date(d.d ?? d.date),
                };
                val[key] = d.v ?? d.value;
                return val;
            });

            // Over a certain number of points the spread operator doesn't work
            if (newPoints.length > 100000) {
                newPoints.forEach((np) => {
                    flatData.push(np);
                });
            } else {
                flatData.push(...newPoints);
            }
        });

        if (this.debugTiming) {
            console.info(`done flattening ${Date.now() - startTime}`);
        }

        // Sort the freshly flattened data
        flatData.sort((a, b) => a.date.valueOf() - b.date.valueOf());

        if (this.debugTiming) {
            console.info(`done sorting ${Date.now() - startTime}`);
        }

        //set null values. This prevents graphing libraries from interpolating huge distances if a sensor goes offline
        const lastDataTimeForEachKey = {};
        let lastNullCheck = new Date(0);
        for (let i = 0; i < flatData.length; i++) {
            const point = flatData[i];
            const currentTime = new Date(point.date);

            const currentTimeValue = currentTime.valueOf();

            const keysOnPoint = Object.keys(point);
            keysOnPoint.forEach((k) => {
                if (k !== "date") {
                    lastDataTimeForEachKey[k] = currentTime.valueOf();
                }
            });
            //only run this every 20 minutes worth of data points.
            if (Math.abs(currentTimeValue - lastNullCheck.valueOf()) > 1000 * 60 * 20) {
                lastNullCheck = new Date(currentTimeValue);
                Object.keys(lastDataTimeForEachKey).forEach((k) => {
                    if (k !== "date") {
                        const maxTimeTillNull =
                            DataTypeConfig.DataTypeConfigurations[this.dataKeyMetadataLookup[k].dataType]
                                ?.msTillIrrelevant || dayMs;

                        if (currentTimeValue - lastDataTimeForEachKey[k] > maxTimeTillNull) {
                            flatData[i][k] = null;
                        }
                    }
                });
            }
        }

        let i = 0;
        if (this.debugTiming) {
            console.info(`done prepping for insertion ${Date.now() - startTime}`);
        }

        //insert the data
        while (i < flatData.length) {
            const iAtbeginningOfLoop = i;
            // sleep for 0ms occasionally to avoid lagging the main thread.
            if (Math.random() < 0.001) {
                await sleep(0);
            }
            let pointOfInsertion = -1;
            let lastDate = new Date(Date.now() + 1000 * 60 * 60 * 365 * 5);
            let cursorPoint = {};
            let prevPoint;

            // Find the bounds of the next insertion
            if (this.data.value.length > 2) {
                const prevPoint = this.data.getClosestPointToValue(flatData[i].date.valueOf(), "lower");
                if (prevPoint) {
                    pointOfInsertion = this.data.value.indexOf(prevPoint);
                    const previousCursor = prevPoint.getAllData();
                    cursorPoint = Object.assign(cursorPoint, { ...previousCursor });
                }
                let nextPoint = pointOfInsertion + 1;
                if (nextPoint >= this.data.value.length) {
                    nextPoint = undefined;
                }
                if (nextPoint) {
                    lastDate = this.data.value[nextPoint].date;
                }
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let dataDelta: Record<string, any> = {};
            let countDownToAnchor = 0;
            const pointsToInsert = [];
            let dataPointStart = new Date(flatData[i].date.valueOf());

            //Do actual insertion
            while (i < flatData.length && flatData[i].date.valueOf() < lastDate.valueOf()) {
                //Backshift any data that would otherwise be lost. IF there is actually data between the points
                //it should be lost, but if there are two points close together, the previous point should be backshifted
                //instead of being destroyed.

                Object.assign(dataDelta, flatData[i]);
                Object.assign(cursorPoint, flatData[i]);
                if (dataPointStart === undefined) {
                    dataPointStart = new Date(flatData[i].date.valueOf());
                }
                let makeADataPoint = true;
                if (flatData[i + 1]) {
                    makeADataPoint =
                        flatData[i + 1].date.valueOf() - dataPointStart.valueOf() > this.maximumMillisecondsDeltaT;
                }
                if (makeADataPoint) {
                    countDownToAnchor--;
                    const newDataPoint = new DataPoint(new Date(dataDelta.date), this, {
                        ...dataDelta,
                    });
                    newDataPoint.prevPoint = prevPoint;
                    if (countDownToAnchor <= 0) {
                        newDataPoint.anchorPoint = true;
                        newDataPoint.anchorData = { ...cursorPoint };
                        countDownToAnchor = this.anchorEveryXPoints;
                    }
                    dataDelta = {};
                    pointsToInsert.push(newDataPoint);
                    prevPoint = newDataPoint;

                    dataPointStart = undefined;
                }
                i++;
            }
            //Splice in new Data
            //also, ensure prevPoints are kept up to date
            if (pointsToInsert.length > 0) {
                let newValue = [...this.data.value];
                if (pointOfInsertion >= 0) {
                    const tail = newValue.splice(pointOfInsertion + 1);
                    if (tail.length > 0) {
                        tail[0].prevPoint = pointsToInsert[pointsToInsert.length - 1];
                    }
                    newValue.push(...pointsToInsert);
                    newValue.push(...tail);
                } else {
                    pointsToInsert.push(...newValue);
                    if (newValue[0]) {
                        newValue[0].prevPoint = pointsToInsert[pointsToInsert.length - 1];
                    }
                    newValue = [...pointsToInsert];
                }
                this.data.value = newValue;
            }

            // Guard against insertion loops that fail to advance the index.
            if (i === iAtbeginningOfLoop) {
                i++;
            }
        }

        if (this.debugTiming) {
            console.info(`done insertion ${Date.now() - startTime}`);
        }
        this.data.validate(this.data.value);
        if (this.debugTiming) {
            console.info(`done validation ${Date.now() - startTime}`);
        }
    }

    /**
     * If this is false, any historical data objects that do not have a relevant
     * zone or have any empty list of relevant zones will be discarded
     * @type {boolean}
     */
    allowBlankZone = true;

    async updateMostRecentData(timeToGoBackTo?: Date): Promise<void> {
        const resolution = await this._waitInQueue();
        try {
            const startTime = timeToGoBackTo ?? new Date(Date.now() - minuteMs * 6);
            const endTime = new Date(Date.now());
            let dataToAdd = await HistoricalDataAPI.getData(
                startTime,
                endTime,
                Array.isArray(this.sources)
                    ? this.sources.map((v) => {
                          if (v instanceof ModelBase) {
                              return v.id;
                          } else {
                              // @ts-ignore
                              return v.toString();
                          }
                      })
                    : undefined,
                this.dataTypes.map((v) => v.toString()),
                {
                    compress: this.compressedRequests,
                    relevantZoneIDs: this.relevantZoneIDs,
                },
            );

            dataToAdd = dataToAdd.filter((historicalDataObject) => {
                if (
                    historicalDataObject.relevantZones &&
                    Array.isArray(historicalDataObject.relevantZones) &&
                    historicalDataObject.relevantZones.length > 0
                ) {
                    if (
                        this.acceptableZones &&
                        Array.isArray(this.acceptableZones) &&
                        this.acceptableZones.length > 0
                    ) {
                        return historicalDataObject.relevantZones.some((rz) => this.acceptableZones.includes(rz));
                    }
                } else {
                    if (!this.allowBlankZone) {
                        return false;
                    }
                }
                return true;
            });

            if (dataToAdd) {
                await this._addDataFromServer(dataToAdd);
            }

            resolution();
        } catch (e) {
            resolution();
            throw e;
        }
    }

    /**
     *
     * @param start
     * @param end
     * @return {Promise<any>}
     */
    async getData(start: Date, end: Date): Promise<void> {
        const resolution = await this._waitInQueue();

        let postponedStart = new Date(start.valueOf());
        let prematureEnd = new Date(end.valueOf());
        let adjusted = true;
        while (adjusted) {
            adjusted = false;
            for (let i = 0; i < this.downloadedRange.length; i++) {
                const cur = this.downloadedRange[i];
                if (cur.start.valueOf() <= postponedStart.valueOf() && cur.end.valueOf() > postponedStart.valueOf()) {
                    postponedStart = new Date(cur.end.valueOf());
                    adjusted = true;
                }
                if (cur.start.valueOf() < prematureEnd.valueOf() && cur.end.valueOf() >= prematureEnd.valueOf()) {
                    prematureEnd = new Date(cur.start.valueOf());
                    adjusted = true;
                }
                if (postponedStart.valueOf() >= prematureEnd.valueOf()) {
                    adjusted = false;
                }
            }
        }

        if (this.downloadedRange.length > 0) {
            const timeOfLastDownloadedDatapoint = Math.max(
                0,
                ...this.downloadedRange.map((range) => range.end.valueOf()),
            );
            const timeOfFirstDownloadedDatapoint = Math.min(
                Number.MAX_SAFE_INTEGER,
                ...this.downloadedRange.map((range) => range.start.valueOf()),
            );

            // this code is a hacky fix I have no faith in, but it's better than nothing.
            if (start.valueOf() < timeOfFirstDownloadedDatapoint && end.valueOf() > timeOfLastDownloadedDatapoint) {
                console.error(
                    "Potential issue with trying to expand the scope of a historical DB on both sides at the same time. This can be unstable.",
                );
                const promises = [
                    this.getData(start, new Date(timeOfFirstDownloadedDatapoint.valueOf())).catch((e) => {
                        console.error("Error loading data before firstDataPoint in sub call of getData", e);
                        throw e;
                    }),
                    this.getData(new Date(timeOfLastDownloadedDatapoint.valueOf()), end).catch((e) => {
                        console.error("Error loading data after lastDataPoint in sub call of getData", e);
                        throw e;
                    }),
                ];
                resolution();
                await Promise.all(promises);
                return;
            }
        }

        // if the date range we are looking for is a subset of a date range we have already
        // downloaded, don't download it again.
        for (let i = 0; i < this.downloadedRange.length; i++) {
            const cur = this.downloadedRange[i];
            if (cur.start.valueOf() < postponedStart.valueOf() && cur.end.valueOf() > prematureEnd.valueOf()) {
                resolution();
                return;
            }
        }

        if (postponedStart.valueOf() < prematureEnd.valueOf()) {
            try {
                let dataToAdd = await HistoricalDataAPI.getData(
                    postponedStart,
                    prematureEnd,
                    Array.isArray(this.sources)
                        ? this.sources.map((v) => {
                              if (v instanceof ModelBase) {
                                  return v.id;
                              } else {
                                  // @ts-ignore
                                  return v.toString();
                              }
                          })
                        : undefined,
                    this.dataTypes.map((v) => v.toString()),
                    {
                        compress: this.compressedRequests,
                        relevantZoneIDs: this.relevantZoneIDs,
                    },
                );

                this.downloadedRange.push({
                    start: new Date(start.valueOf()),
                    end: new Date(end.valueOf()),
                });

                dataToAdd = dataToAdd.filter((hdo) => {
                    if (hdo.relevantZones && Array.isArray(hdo.relevantZones) && hdo.relevantZones.length > 0) {
                        if (
                            this.acceptableZones &&
                            Array.isArray(this.acceptableZones) &&
                            this.acceptableZones.length > 0
                        ) {
                            return hdo.relevantZones.some((rz) => this.acceptableZones.includes(rz));
                        }
                    } else {
                        if (!this.allowBlankZone) {
                            return false;
                        }
                    }
                    return true;
                });

                if (dataToAdd) {
                    await this._addDataFromServer(dataToAdd);
                }
            } catch (e) {
                console.warn(e);
                console.warn("Could not fetch this data from the server. Unkown error");
            }
        }

        resolution();
        return;
    }

    isDataLoaded(start: Date, end: Date): boolean {
        let postponedStart = new Date(start.valueOf());
        let prematureEnd = new Date(end.valueOf());
        let adjusted = true;
        while (adjusted) {
            adjusted = false;
            for (let i = 0; i < this.downloadedRange.length; i++) {
                const cur = this.downloadedRange[i];
                if (cur.start.valueOf() <= postponedStart.valueOf() && cur.end.valueOf() > postponedStart.valueOf()) {
                    postponedStart = new Date(cur.end.valueOf());
                    adjusted = true;
                }
                if (cur.start.valueOf() < prematureEnd.valueOf() && cur.end.valueOf() >= prematureEnd.valueOf()) {
                    prematureEnd = new Date(cur.start.valueOf());
                    adjusted = true;
                }
                if (postponedStart.valueOf() >= prematureEnd.valueOf()) {
                    adjusted = false;
                }
            }
        }
        return !(postponedStart.valueOf() < prematureEnd.valueOf());
    }

    /**
     * Finds the data key based on parameters. Always returns either exactly one data key or no data keys.
     * @param model
     * @param dataType
     * @param positionHash
     * @return {string}
     */
    getDataKeyForModel(
        model: ModelBase | string,
        dataType: string = undefined,
        positionHash: number = undefined,
    ): string {
        const allKeys = Object.keys(this.dataKeyMetadataLookup);
        let querySourceID = model;
        if (model instanceof ModelBase) {
            querySourceID = model.id;
        }
        for (let i = 0; i < allKeys.length; i++) {
            const key = allKeys[i];
            const keySourceID = this.dataKeyMetadataLookup[key].source;
            const keyPositionHash = this.dataKeyMetadataLookup[key].positionHash;
            const keyDataType = this.dataKeyMetadataLookup[key].dataType;
            if (
                querySourceID === keySourceID &&
                (!dataType || keyDataType === dataType) &&
                (!positionHash || positionHash === keyPositionHash)
            ) {
                return key;
            }
        }
    }

    /**
     * @deprecated
     * @return {Promise<any>}
     */
    async postData() {
        const resolution = await this._waitInQueue();

        resolution();
        return void 0;
    }

    exportToCSV() {
        return;
    }
}
